import React from "react";
import { useTranslation } from "react-i18next";
import NestedTable from "../components/NestedTable";
import {
  DECLARATION_FORM_DEBOUNCE,
  DeclarationGoodsItemPackagingForm,
  FieldsRefs,
  SetDeclarationFormFieldRefFunction,
} from "../Declaration";
import { LanguageType } from "i18n";
import { FormTextField, MRTable, Typography } from "@periplus/ui-library";
import NestedTableFormId from "../components/NestedTable/NestedTableFormId";
import FormField from "../components/FormField/FormField";
import EdecDomainFormAutocomplete from "domain/edec/components/EdecDomainFormAutocomplete";
import NestedTableFormContainer from "../components/NestedTable/NestedTableFormContainer";
import { EdecData } from "domain/edec/useGetEdecMasterData";

interface Props {
  name: string;
  edecData: EdecData;
  onReturn: () => void;
  setFormFieldRef: SetDeclarationFormFieldRefFunction;
  onFormFieldFocus: (value: string) => void;
  onFormFieldBlur: () => void;
  pageContainerWidth: number;
  formFieldsRefs: React.MutableRefObject<FieldsRefs>;
  pageContainerHeight: number;
}

export default function ({
  name,
  edecData,
  onReturn,
  setFormFieldRef,
  onFormFieldFocus,
  onFormFieldBlur,
  pageContainerWidth,
  formFieldsRefs,
  pageContainerHeight,
}: Props) {
  const { t, i18n } = useTranslation("declarationNew");
  const language = i18n.language as LanguageType;

  const previousDocumentsColumns = React.useMemo<
    MRTable.MRT_ColumnDef<DeclarationGoodsItemPackagingForm>[]
  >(
    () => [
      {
        header: t("Pkg Type"),
        accessorKey: "packaging_type",
      },
      {
        header: t("Desc"),
        Cell: ({ row }) =>
          edecData.edec_domains["packagingType"].find(
            (el) => el.value === row.original.packaging_type
          )?.[`meaning_${language}`],
      },
      {
        header: t("Quantity"),
        accessorKey: "packaging_quantity",
      },
      {
        header: t("Pck.Ref. Nr"),
        accessorKey: "packaging_reference_number",
      },
    ],
    [t, language, edecData.edec_domains]
  );

  return (
    <NestedTable
      name={name}
      label={t("Packaging")}
      columns={previousDocumentsColumns}
      defaultItem={{
        packaging_type: null,
        packaging_quantity: null,
        packaging_reference_number: null,
      }}
      onReturn={onReturn}
      formFieldsRefs={formFieldsRefs}
      pageContainerHeight={pageContainerHeight}
      renderForm={({
        selectedItemRowNumber,
        getTableGeneralFormFieldProps,
        ref,
      }) => (
        <NestedTableFormContainer
          ref={ref}
          sx={(theme) => ({
            gridTemplateColumns:
              pageContainerWidth > theme.breakpoints.values.md
                ? "auto 1fr auto 1fr"
                : "auto 1fr",
            alignItems: "start",
          })}
        >
          <Typography>{t("Row Nr.")}</Typography>
          <NestedTableFormId id={selectedItemRowNumber} />
          <FormField
            {...getTableGeneralFormFieldProps("packaging_type")}
            label={t("Type")}
            onFocus={onFormFieldFocus}
            onBlur={onFormFieldBlur}
            setFormFieldRef={setFormFieldRef}
            sx={{
              gridColumn: "1",
            }}
            renderInput={({ inputRef, ...rest }) => (
              <EdecDomainFormAutocomplete
                {...rest}
                options={edecData.edec_domains["packagingType"]}
                InputProps={{
                  inputRef,
                }}
              />
            )}
          />
          <FormField
            {...getTableGeneralFormFieldProps("packaging_quantity")}
            label={t("Quantity")}
            onFocus={onFormFieldFocus}
            onBlur={onFormFieldBlur}
            setFormFieldRef={setFormFieldRef}
            fillByClickMode
            renderInput={({ ...rest }) => (
              <FormTextField
                {...rest}
                debounce={DECLARATION_FORM_DEBOUNCE}
                type="number"
              />
            )}
          />
          <FormField
            {...getTableGeneralFormFieldProps("packaging_reference_number")}
            label={t("Pck.Ref. Nr")}
            onFocus={onFormFieldFocus}
            onBlur={onFormFieldBlur}
            setFormFieldRef={setFormFieldRef}
            fillByClickMode
            renderInput={({ ...rest }) => (
              <FormTextField
                {...rest}
                debounce={DECLARATION_FORM_DEBOUNCE}
                sx={{
                  gridColumn: "2/-1",
                }}
              />
            )}
          />
        </NestedTableFormContainer>
      )}
    />
  );
}
